import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { Database } from 'supabase/database.types'
import { useAuth } from 'src/shared/hooks/authProvider'
import { queryKeys } from './queryKeys'

export type Like_ = Database['public']['Tables']['likes']['Row']
export type InsertLike = Database['public']['Tables']['likes']['Insert']

export const useLikesApi = ({ productIds }: { productIds?: number[] }) => {
  const supabase = useSupabaseClient()
  // Get like counts for products
  const { data: likeCounts } = useQuery({
    queryKey: [queryKeys.likes, 'counts', productIds],
    queryFn: async () => {
      if (!productIds || productIds.length === 0)
        return new Map<number, number>()

      const { data, error } = await supabase
        .from('likes')
        .select('product_id, like')
        .in('product_id', productIds)
        .eq('like', true)

      if (error) throw error

      const counts = new Map<number, number>()
      data?.forEach((like) => {
        if (like.product_id !== null) {
          const currentCount = counts.get(like.product_id) || 0
          counts.set(like.product_id, currentCount + 1)
        }
      })

      return counts
    },
    enabled: !!productIds && productIds.length > 0,
  })

  return {
    likeCounts: likeCounts || new Map<number, number>(),
  }
}

export const useUserLikesApi = ({ productIds }: { productIds?: number[] }) => {
  const supabase = useSupabaseClient()
  const { session } = useAuth()

  // Get user's liked products
  const { data: userLikes } = useQuery({
    queryKey: [queryKeys.likes, 'user', session?.user?.id, productIds],
    queryFn: async () => {
      if (!session?.user?.id || !productIds || productIds.length === 0) {
        return new Set<number>()
      }

      const { data, error } = await supabase
        .from('likes')
        .select('product_id')
        .eq('user_id', session.user.id)
        .eq('like', true)
        .in('product_id', productIds)

      if (error) throw error

      const likedProductIds = new Set<number>()
      data?.forEach((like) => {
        if (like.product_id !== null) {
          likedProductIds.add(like.product_id)
        }
      })

      return likedProductIds
    },
    enabled: !!session?.user?.id && !!productIds && productIds.length > 0,
  })

  return {
    userLikes: userLikes || new Set<number>(),
  }
}

export const useToggleLikeApi = () => {
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()
  const { session } = useAuth()

  const toggleLike = useMutation({
    mutationFn: async ({
      productId,
      currentLikeState,
    }: {
      productId: number
      currentLikeState: boolean
    }) => {
      if (!session?.user?.id) {
        throw new Error('User must be authenticated to like products')
      }

      if (currentLikeState) {
        // Unlike: delete the like
        const { error } = await supabase
          .from('likes')
          .delete()
          .eq('user_id', session.user.id)
          .eq('product_id', productId)
          .eq('like', true)

        if (error) throw error
      } else {
        // Like: upsert (insert or update if exists)
        const { error } = await supabase.from('likes').upsert(
          {
            user_id: session.user.id,
            product_id: productId,
            like: true,
          },
          {
            onConflict: 'user_id,product_id',
          }
        )

        if (error) throw error
      }

      return { productId, newLikeState: !currentLikeState }
    },
    onSuccess: () => {
      // Invalidate all like-related queries
      queryClient.invalidateQueries({ queryKey: [queryKeys.likes] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.products] })
    },
  })

  return {
    toggleLike,
  }
}
